import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Message from "@intility/bifrost-react/Message";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import { useState } from "react";
import { parse as parseYaml } from "yaml";
import { checkRelationship, writeTuples } from "~/api/client";
import type { TupleKey } from "~/api/fga-types";
import { PageLayout } from "~/components/layout/PageLayout";
import { relationBadgeState } from "~/lib/relationBadges";

interface YamlTuple {
  user: string;
  relation: string;
  object: string;
}

interface YamlCheckEntry {
  user: string;
  object: string;
  assertions: Record<string, boolean>;
}

interface YamlTest {
  name: string;
  check: YamlCheckEntry[];
}

interface YamlStoreTest {
  tuples?: YamlTuple[];
  tests?: YamlTest[];
}

interface AssertionResult {
  testName: string;
  user: string;
  relation: string;
  object: string;
  expected: boolean;
  actual: boolean | null;
  passed: boolean;
  error?: string;
}

const EXAMPLE_YAML = `tuples:
  - user: user:alice
    relation: viewer
    object: document:readme
  - user: user:bob
    relation: editor
    object: document:readme

tests:
  - name: alice can view
    check:
      - user: user:alice
        object: document:readme
        assertions:
          viewer: true
          editor: false`;

export default function StoreTests() {
  const [yamlInput, setYamlInput] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<YamlStoreTest | null>(null);

  // Import state
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Test state
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<AssertionResult[]>([]);
  const [testsDone, setTestsDone] = useState(false);

  const handleParse = () => {
    setParseError(null);
    setParsed(null);
    setImportDone(false);
    setTestsDone(false);
    setResults([]);

    try {
      const data = parseYaml(yamlInput.trim()) as YamlStoreTest;
      if (!data || typeof data !== "object") {
        throw new Error("YAML must be an object with tuples and/or tests");
      }
      if (data.tuples && !Array.isArray(data.tuples)) {
        throw new Error("tuples must be an array");
      }
      if (data.tests && !Array.isArray(data.tests)) {
        throw new Error("tests must be an array");
      }
      for (const t of data.tuples ?? []) {
        if (!t.user || !t.relation || !t.object) {
          throw new Error("Each tuple must have user, relation, and object");
        }
      }
      for (const test of data.tests ?? []) {
        if (!test.name || !Array.isArray(test.check)) {
          throw new Error("Each test must have a name and check array");
        }
      }
      setParsed(data);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Invalid YAML");
    }
  };

  const handleImport = async () => {
    if (!parsed?.tuples?.length) return;
    setImporting(true);
    setImportError(null);

    try {
      const writes: TupleKey[] = parsed.tuples.map((t) => ({
        user: t.user,
        relation: t.relation,
        object: t.object,
      }));
      await writeTuples({ writes });
      setImportDone(true);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleRunTests = async () => {
    if (!parsed?.tests?.length) return;
    setRunning(true);
    setTestsDone(false);
    setResults([]);

    const allResults: AssertionResult[] = [];

    for (const test of parsed.tests) {
      for (const entry of test.check) {
        for (const [relation, expected] of Object.entries(entry.assertions)) {
          try {
            const res = await checkRelationship({
              tupleKey: {
                user: entry.user,
                relation,
                object: entry.object,
              },
            });
            allResults.push({
              testName: test.name,
              user: entry.user,
              relation,
              object: entry.object,
              expected,
              actual: res.allowed,
              passed: res.allowed === expected,
            });
          } catch (err) {
            allResults.push({
              testName: test.name,
              user: entry.user,
              relation,
              object: entry.object,
              expected,
              actual: null,
              passed: false,
              error: err instanceof Error ? err.message : "Check failed",
            });
          }
          // Update results progressively
          setResults([...allResults]);
        }
      }
    }

    setRunning(false);
    setTestsDone(true);
  };

  const passCount = results.filter((r) => r.passed).length;
  const failCount = results.filter((r) => !r.passed).length;

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Tests" },
      ]}
      title="Store Tests"
      description="Import tuples and run authorization checks using the OpenFGA YAML test format."
    >
      <Section>
        <Section.Header>Test Definition (YAML)</Section.Header>
        <Section.Content>
          <textarea
            value={yamlInput}
            onChange={(e) => setYamlInput(e.target.value)}
            placeholder={EXAMPLE_YAML}
            rows={20}
            spellCheck={false}
            style={{
              width: "100%",
              fontFamily: "monospace",
              fontSize: 13,
              lineHeight: 1.5,
              resize: "vertical",
              padding: 12,
              borderRadius: 4,
              border: "1px solid var(--bf-border-color)",
              backgroundColor: "var(--bf-base-bg)",
              color: "var(--bf-base-color)",
              tabSize: 2,
            }}
          />
        </Section.Content>
        {parseError && (
          <Section.Content borderTop>
            <Message state="alert" header="Parse Error">
              {parseError}
            </Message>
          </Section.Content>
        )}
        <Section.Content borderTop>
          <Button onClick={handleParse} disabled={!yamlInput.trim()}>
            Parse YAML
          </Button>
        </Section.Content>
      </Section>

      {parsed && (
        <Section>
          <Section.Header>Parsed</Section.Header>
          <Section.Content>
            <p className="bf-p">
              <Badge state="neutral">{parsed.tuples?.length ?? 0}</Badge> tuples
              &middot;{" "}
              <Badge state="neutral">{parsed.tests?.length ?? 0}</Badge> tests
              &middot;{" "}
              <Badge state="neutral">
                {parsed.tests?.reduce(
                  (sum, t) =>
                    sum +
                    t.check.reduce(
                      (s, c) => s + Object.keys(c.assertions).length,
                      0,
                    ),
                  0,
                ) ?? 0}
              </Badge>{" "}
              assertions
            </p>
          </Section.Content>
          <Section.Content borderTop>
            <div style={{ display: "flex", gap: 8 }}>
              {(parsed.tuples?.length ?? 0) > 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importing || importDone}
                >
                  {importing
                    ? "Importing..."
                    : importDone
                      ? "Tuples Imported"
                      : `Import ${parsed.tuples?.length} Tuples`}
                </Button>
              )}
              {(parsed.tests?.length ?? 0) > 0 && (
                <Button
                  onClick={handleRunTests}
                  disabled={running}
                  variant="filled"
                >
                  {running ? "Running..." : "Run Tests"}
                </Button>
              )}
            </div>
          </Section.Content>
          {importError && (
            <Section.Content borderTop>
              <Message state="alert" header="Import Error">
                {importError}
              </Message>
            </Section.Content>
          )}
          {importDone && (
            <Section.Content borderTop>
              <Message state="success" header="Import Complete">
                {parsed.tuples?.length} tuple(s) written successfully.
              </Message>
            </Section.Content>
          )}
        </Section>
      )}

      {results.length > 0 && (
        <Section>
          <Section.Header>
            Test Results{" "}
            {testsDone && (
              <Badge state={failCount === 0 ? "success" : "alert"}>
                {passCount}/{results.length} passed
              </Badge>
            )}
            {running && (
              <Badge state="neutral">{results.length} / running...</Badge>
            )}
          </Section.Header>
          <Table noBorder>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: 40 }} />
                <Table.HeaderCell>Test</Table.HeaderCell>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Relation</Table.HeaderCell>
                <Table.HeaderCell>Object</Table.HeaderCell>
                <Table.HeaderCell>Expected</Table.HeaderCell>
                <Table.HeaderCell>Actual</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {results.map((r) => (
                <Table.Row
                  key={`${r.user}-${r.relation}-${r.object}-${r.expected}`}
                >
                  <Table.Cell>
                    {r.passed ? (
                      <Badge state="success" pill>
                        PASS
                      </Badge>
                    ) : (
                      <Badge state="alert" pill>
                        FAIL
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>{r.testName}</Table.Cell>
                  <Table.Cell>
                    <code>{r.user}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge state={relationBadgeState(r.relation, "access")}>
                      {r.relation}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <code>{r.object}</code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge state={r.expected ? "success" : "neutral"} pill>
                      {String(r.expected)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {r.error ? (
                      <Badge state="alert" pill>
                        error
                      </Badge>
                    ) : (
                      <Badge state={r.actual ? "success" : "neutral"} pill>
                        {String(r.actual)}
                      </Badge>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Section>
      )}
    </PageLayout>
  );
}
